import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBlocker } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { requestErrorMessage } from "@/lib/requestError";
import { UnsavedContext } from "./unsavedChanges";
export { useUnsavedChanges } from "./unsavedChanges";

export function UnsavedChangesProvider({ children }) {
  const { user } = useAuth();
  const enabled = Boolean(user && !user.must_change_password);
  const entries = useRef(new Map());
  const bypass = useRef(false);
  const working = useRef(false);
  const scope = user ? `${user.id}:${user.company_id}:${user.role}:${user.platform_role}:${Boolean(user.must_change_password)}` : "signed-out";
  const lifetime = useRef({ scope, active: true, attempt: 0 });
  const lastScope = useRef(scope);
  if (lifetime.current.scope !== scope) {
    lifetime.current.scope = scope;
    lifetime.current.attempt++;
  }
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    const current = lifetime.current;
    current.active = true;
    return () => { current.active = false; current.attempt++; };
  }, []);
  useEffect(() => {
    working.current = false; setSaving(false); setPending(null); setError("");
  }, [scope]);
  const dirtyEntries = useCallback(ids => [...entries.current.entries()]
    .filter(([id]) => !ids || ids.includes(id)).map(([, read]) => read())
    .filter(entry => entry.dirty && !entry.disabled), []);
  const register = useCallback((id, read) => {
    entries.current.set(id, read);
    return () => entries.current.delete(id);
  }, []);
  const requestLeave = useCallback((action, ids = null) => {
    if (!enabled || bypass.current || !dirtyEntries(ids).length) { action(); return; }
    setError("");
    setPending({ action, ids, router: false });
  }, [dirtyEntries, enabled]);
  const blocker = useBlocker(({ currentLocation, nextLocation }) => enabled && !bypass.current &&
    (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search) &&
    dirtyEntries().length > 0);

  useEffect(() => {
    if (lastScope.current !== scope) {
      lastScope.current = scope;
      setPending(null);
      if (blocker.state === "blocked") blocker.reset();
    } else if (!enabled) {
      setPending(null);
      if (blocker.state === "blocked") blocker.proceed();
    } else if (blocker.state === "blocked") {
      setError("");
      setPending(current => current?.router ? current : { action: blocker.proceed, ids: null, router: true });
    }
  }, [blocker, enabled, scope]);

  useEffect(() => {
    const beforeUnload = event => {
      if (!enabled || !dirtyEntries().length) return;
      event.preventDefault(); event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dirtyEntries, enabled]);

  const keepEditing = () => {
    if (working.current) return;
    if (pending?.router && blocker.state === "blocked") blocker.reset();
    setPending(null); setError("");
  };
  const proceed = () => {
    const action = pending?.action;
    setPending(null); setError("");
    bypass.current = true;
    try { action?.(); } finally { queueMicrotask(() => { bypass.current = false; }); }
  };
  const saveAndLeave = async () => {
    if (working.current) return;
    working.current = true; setSaving(true); setError("");
    const current = lifetime.current;
    const attempt = ++current.attempt;
    const isCurrent = () => current.active && current.attempt === attempt && current.scope === scope;
    try {
      for (const entry of dirtyEntries(pending?.ids)) {
        if (!isCurrent()) return;
        if (entry.busy || !entry.onSave) {
          setError("Changes were not saved. Keep editing to review the form, or try again."); return;
        }
        const saved = await entry.onSave(isCurrent);
        if (!isCurrent()) return;
        if (saved !== true) { setError("Changes were not saved. Keep editing to review the form, or try again."); return; }
      }
      if (!isCurrent()) return;
      proceed();
    } catch (failure) {
      if (isCurrent()) setError(requestErrorMessage(failure, "Changes were not saved. Keep editing to review the form, or try again."));
    } finally { if (isCurrent()) { working.current = false; setSaving(false); } }
  };
  const discardAndLeave = () => {
    if (working.current) return;
    for (const entry of dirtyEntries(pending?.ids)) entry.onDiscard?.();
    proceed();
  };
  const dirty = pending ? dirtyEntries(pending.ids) : [];
  const canSave = dirty.every(entry => typeof entry.onSave === "function");
  const busy = saving || dirty.some(entry => entry.busy);
  const value = useMemo(() => ({ register, requestLeave }), [register, requestLeave]);
  return <UnsavedContext.Provider value={value}>
    {children}
    <Dialog open={Boolean(pending)} onOpenChange={open => { if (!open) keepEditing(); }}>
      <DialogContent onEscapeKeyDown={event => { if (saving) event.preventDefault(); }}>
        <DialogHeader><DialogTitle>Unsaved changes</DialogTitle>
          <DialogDescription>{canSave ? "Save your changes before leaving, discard them, or keep editing." : "Keep editing to save these changes using their own controls, or discard them before leaving."}</DialogDescription>
        </DialogHeader>
        {dirty.length > 0 && <p>{[...new Set(dirty.map(entry => entry.label || "Editor"))].join(", ")}</p>}
        {error && <p role="alert">{error}</p>}
        <DialogFooter>
          <Button autoFocus type="button" variant="outline" disabled={saving} onClick={keepEditing}>Keep editing</Button>
          <Button type="button" variant="outline" disabled={busy} onClick={discardAndLeave}>Discard changes</Button>
          {canSave && <Button type="button" disabled={busy} onClick={saveAndLeave}>{saving ? "Saving…" : "Save changes"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </UnsavedContext.Provider>;
}

